'use client';

import classNames from 'classnames';
import { RecaptchaDisclaimer, RecaptchaFormChallenge, RecaptchaFormProvider } from 'google-recaptcha/components';
import Link from 'next/link';
import { JSX, useState } from 'react';

import { Tooltip } from '@videoblocks/design-system';
import { View, ViewOff } from '@videoblocks/react-icons';
import { Button } from '@videoblocks/storywind';

import { PASSWORD_MIN_LENGTH } from '../../../lib/validation/passwordValidation';
import { useFormValidation } from '../../_shared/hooks/useFormValidationV2';
import { useSignUpContext } from '../../_shared/sign-up/SignUpContext';
import { useSubmitSignUpForm } from '../../_shared/sign-up/useSubmitSignUpForm';

interface SignUpFormProps {
    planCode?: string;
    redirectAfterRegister?: string;
}

export default function SignUpForm({ planCode, redirectAfterRegister }: SignUpFormProps): JSX.Element {
    const { error, submitText } = useSignUpContext();
    const [showPassword, setShowPassword] = useState(false);
    const { isFormValid, errors, touched, refs, handleInputChange, handleBlur, handleFocus } = useFormValidation({
        defaultValues: { checkbox: true },
    });

    const INPUT_CLASS =
        'w-full border rounded px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 placeholder:text-gray-700';
    const recaptcha = useSubmitSignUpForm(planCode, redirectAfterRegister);
    // Submittable only when fields are valid AND the reCAPTCHA checkbox is solved; drive both
    // the disabled state and the styling from this one value so they can't disagree.
    const canSubmit = isFormValid && recaptcha.canSubmit;
    return (
        <RecaptchaFormProvider value={recaptcha}>
            <form onSubmit={recaptcha.handleSubmit} aria-label="Registration Form" className="space-y-4">
                {error && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded py-2 px-3">
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}

                <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-1">
                        Email Address
                    </label>
                    <input
                        ref={refs.emailInputRef}
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        autoComplete="email"
                        required
                        onChange={handleInputChange}
                        onFocus={() => handleFocus('email')}
                        onBlur={() => handleBlur('email')}
                        className={classNames(INPUT_CLASS, {
                            'border-gray-400': !errors.email || !touched.email,
                            'border-red-500': errors.email && touched.email,
                        })}
                    />
                    {touched.email && errors.email && <div className="mt-1 text-sm text-red-600">{errors.email}</div>}
                </div>

                <div>
                    <div className="flex justify-between items-center mb-1">
                        <label htmlFor="password" className="block text-sm font-semibold text-gray-900">
                            Password
                        </label>
                    </div>
                    <div className="relative">
                        <input
                            // eslint-disable-next-line react-hooks/refs
                            ref={refs.passwordInputRef}
                            id="password"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
                            autoComplete="new-password"
                            required
                            minLength={PASSWORD_MIN_LENGTH}
                            onChange={handleInputChange}
                            onFocus={() => handleFocus('password')}
                            onBlur={() => handleBlur('password')}
                            data-testid="password-input"
                            className={classNames('pr-10', INPUT_CLASS, {
                                'border-gray-400': !errors.password || !touched.password,
                                'border-red-500': errors.password && touched.password,
                            })}
                        />
                        <Tooltip
                            title={showPassword ? 'Hide password' : 'Show password'}
                            trigger={
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setShowPassword(!showPassword);
                                        }
                                    }}
                                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset focus:text-gray-700"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    tabIndex={0}
                                >
                                    {showPassword ? <ViewOff className="h-4 w-4" /> : <View className="h-4 w-4" />}
                                </button>
                            }
                        />
                    </div>
                    {touched.password && errors.password && (
                        <div className="mt-1 text-sm text-red-600">{errors.password}</div>
                    )}
                </div>

                <div className="mb-6">
                    <div className="flex items-start">
                        <input
                            // eslint-disable-next-line react-hooks/refs
                            ref={refs.checkboxRef}
                            id="agreement-checkbox"
                            name="agreement-checkbox"
                            type="checkbox"
                            role="checkbox"
                            required
                            onChange={handleInputChange}
                            onFocus={() => handleFocus('checkbox')}
                            onBlur={() => handleBlur('checkbox')}
                            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="agreement-checkbox" className="ml-3 text-sm text-gray-600">
                            I have read and accept the{' '}
                            <Link className="text-gray-700 underline" href="/terms-of-service">
                                Terms of Service
                            </Link>
                            ,{' '}
                            <Link className="text-gray-700 underline" href="/privacy">
                                Privacy Policy
                            </Link>
                            , and the License Agreement that applies to my plan:{' '}
                            <Link className="text-gray-700 underline" href="/license/individual-license">
                                Individual License
                            </Link>{' '}
                            /{' '}
                            <Link className="text-gray-700 underline" href="/license/business-license">
                                Business License
                            </Link>
                        </label>
                    </div>
                    {touched.checkbox && errors.checkbox && (
                        <div className="mt-1 text-sm text-red-600">{errors.checkbox}</div>
                    )}
                </div>

                <RecaptchaFormChallenge />

                <Button
                    role="button"
                    type="submit"
                    disabled={!canSubmit}
                    className={classNames('w-full py-3 px-4 rounded font-medium transition-colors cursor-pointer', {
                        'bg-gray-300 text-gray-500 cursor-not-allowed': !canSubmit,
                        'bg-yellow-400 hover:bg-yellow-600 text-gray-900': canSubmit,
                    })}
                >
                    {submitText}
                </Button>

                <RecaptchaDisclaimer className="text-center" />
            </form>
        </RecaptchaFormProvider>
    );
}
